
import { useState, useEffect } from 'react';
import { api } from '@/lib/api/client';
import { LoadingSpinner } from '@/components/ui/loading-spinner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import {
  BarChart3,
  TrendingUp,
  Eye,
  MessageCircle,
  Heart,
  Share,
  Clock,
  Users,
  Download
} from 'lucide-react';
import type { StreamAnalytics } from '@/lib/shared-types/api/endpoints/admin';

interface ContentAnalyticsProps {
  streams?: StreamAnalytics[];
}

// Analytics data is now loaded from API

function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
}

function formatNumber(num: number): string {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toString();
}

export function ContentAnalytics() {
  const [analyticsData, setAnalyticsData] = useState<{
    streams: StreamAnalytics[];
    totalViews: number;
    totalEngagement: number;
    topPerforming: StreamAnalytics[];
    demographics: any;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStreamId, setSelectedStreamId] = useState<string>('');
  const [timeRange, setTimeRange] = useState<string>('30d');

  // Fetch analytics data on mount
  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const response = await api.get('/api/admin/analytics/content');

        if (response.success && response.data) {
          setAnalyticsData(response.data as {
            streams: StreamAnalytics[];
            totalViews: number;
            totalEngagement: number;
            topPerforming: StreamAnalytics[];
            demographics: any;
          });
        } else {
          setError(response.error?.message || 'Failed to load analytics');
        }
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load content analytics');
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const analyticsStreams = analyticsData?.streams || [];

  const selectedStream = analyticsStreams.find(s => String(s.id) === String(selectedStreamId)) || analyticsStreams[0];


  if (isLoading && analyticsStreams.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const totalViews = analyticsData?.totalViews || 0;
  const totalWatchTime = analyticsStreams.reduce((sum, s) => sum + s.totalWatchTime, 0);
  const totalEngagement = analyticsStreams.reduce((sum, s) =>
    sum + s.engagement.likes + s.engagement.comments + s.engagement.shares, 0
  );

  const avgEngagementRate = totalViews > 0
    ? (totalEngagement / totalViews * 100)
    : 0;


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Content Analytics</h1>
          <p className="text-muted-foreground">
            Detailed performance metrics for your streaming content
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="90d">Last 90 days</SelectItem>
              <SelectItem value="1y">Last year</SelectItem>
            </SelectContent>
          </Select>

          <Button variant="outline" size="icon">
            <Download className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Overview Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Views</CardTitle>
            <Eye className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalViews)}</div>
            <p className="text-xs text-muted-foreground">
              Across {analyticsStreams.length} streams
            </p>

          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Watch Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalWatchTime)}h</div>
            <p className="text-xs text-muted-foreground">
              Across {analyticsStreams.length} streams
            </p>
          </CardContent>

        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Engagement</CardTitle>
            <Heart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalEngagement)}</div>
            <p className="text-xs text-muted-foreground">
              {avgEngagementRate.toFixed(1)}% engagement rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Completion</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analyticsStreams.length > 0 ? Math.round(analyticsStreams.reduce((sum, s) => sum + s.completionRate, 0) / analyticsStreams.length) : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Stream completion rate
            </p>
          </CardContent>

        </Card>
      </div>

      {/* Stream Selector and Analytics */}
      <div className="grid gap-6 lg:grid-cols-4">
        {/* Stream List */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Streams</CardTitle>
            <CardDescription>Select a stream to view detailed analytics</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analyticsStreams.map((stream) => (
                <button
                  key={stream.id}
                  onClick={() => setSelectedStreamId(String(stream.id))}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${String(selectedStream?.id) === String(stream.id)

                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:bg-muted/50'
                    }`}
                >

                  <div className="flex items-center justify-between mb-1">
                    <h4 className="font-medium text-sm line-clamp-2">{stream.title}</h4>
                    <Badge variant={
                      stream.status === 'live' ? 'destructive' :
                        stream.status === 'ended' ? 'secondary' : 'default'
                    } className="text-xs">
                      {stream.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Eye className="h-3 w-3" />
                      {formatNumber(stream.views)}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart className="h-3 w-3" />
                      {formatNumber(stream.engagement.likes)}
                    </span>
                  </div>
                </button>
              ))}
              {analyticsStreams.length === 0 && (
                <p className="text-sm text-muted-foreground text-center py-4">No streams available</p>
              )}
            </div>
          </CardContent>

        </Card>

        {/* Stream Analytics */}
        <div className="lg:col-span-3 space-y-6">
          {selectedStream && (
            <>

              {/* Stream Performance */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="h-5 w-5" />
                    {selectedStream.title}
                  </CardTitle>

                  <CardDescription>
                    Performance metrics for this stream
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Eye className="h-4 w-4" />
                        Views
                      </div>
                      <div className="text-2xl font-bold">{formatNumber(selectedStream.views)}</div>
                      <div className="text-xs text-muted-foreground">
                        {Math.round(selectedStream.uniqueViewers)} unique viewers
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="h-4 w-4" />
                        Avg Watch Time
                      </div>
                      <div className="text-2xl font-bold">{formatDuration(Math.round(selectedStream.avgWatchTime))}</div>
                      <div className="text-xs text-muted-foreground">
                        {Math.round(selectedStream.completionRate)}% completion
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Users className="h-4 w-4" />
                        Peak Concurrent
                      </div>
                      <div className="text-2xl font-bold">{selectedStream.peakConcurrent}</div>
                      <div className="text-xs text-muted-foreground">
                        Maximum viewers
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Heart className="h-4 w-4" />
                        Engagement
                      </div>
                      <div className="text-2xl font-bold">
                        {Object.values(selectedStream.engagement).reduce((a, b) => a + b, 0)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        Likes, comments, shares
                      </div>
                    </div>
                  </div>

                </CardContent>
              </Card>

              {/* Engagement Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle>Engagement Breakdown</CardTitle>
                  <CardDescription>Interaction metrics for this stream</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="text-center">
                      <div className="text-3xl font-bold text-red-600 mb-2">
                        {selectedStream.engagement.likes}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                        <Heart className="h-4 w-4" />
                        Likes
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-3xl font-bold text-purple-600 mb-2">
                        {selectedStream.engagement.comments}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                        <MessageCircle className="h-4 w-4" />
                        Comments
                      </div>
                    </div>

                    <div className="text-center">
                      <div className="text-3xl font-bold text-blue-600 mb-2">
                        {selectedStream.engagement.shares}
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center justify-center gap-1">
                        <Share className="h-4 w-4" />
                        Shares
                      </div>
                    </div>
                  </div>

                </CardContent>
              </Card>

              {/* Demographics */}
              <Tabs defaultValue="devices" className="space-y-4">
                <TabsList>
                  <TabsTrigger value="devices">Devices</TabsTrigger>
                  <TabsTrigger value="regions">Regions</TabsTrigger>
                  <TabsTrigger value="age">Age Groups</TabsTrigger>
                </TabsList>

                <TabsContent value="devices" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Device Usage</CardTitle>
                      <CardDescription>Viewer device preferences</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(selectedStream.demographics.deviceTypes).map(([device, percentage]) => (
                          <div key={device} className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="capitalize">{device}</span>
                              <span>{percentage}%</span>
                            </div>
                            <Progress value={percentage} className="h-2" />
                          </div>
                        ))}
                      </div>
                    </CardContent>

                  </Card>
                </TabsContent>

                <TabsContent value="regions" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Regional Distribution</CardTitle>
                      <CardDescription>Viewer locations</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(selectedStream.demographics.regions).map(([region, percentage]) => (
                          <div key={region} className="flex items-center justify-between">
                            <span className="text-sm">{region}</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-muted rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full"
                                  style={{
                                    width: `${percentage}%`
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium w-8 text-right">
                                {percentage}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>

                  </Card>
                </TabsContent>

                <TabsContent value="age" className="space-y-4">
                  <Card>
                    <CardHeader>
                      <CardTitle>Age Demographics</CardTitle>
                      <CardDescription>Viewer age distribution</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {Object.entries(selectedStream.demographics.ageGroups).map(([ageGroup, percentage]) => (
                          <div key={ageGroup} className="flex items-center justify-between">
                            <span className="text-sm">{ageGroup} years</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 bg-muted rounded-full h-2">
                                <div
                                  className="bg-primary h-2 rounded-full"
                                  style={{
                                    width: `${percentage}%`
                                  }}
                                />
                              </div>
                              <span className="text-sm font-medium w-8 text-right">
                                {percentage}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>

                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
